/**
 * Calculates the previous period for a given date range, following a Like-for-Like Comparison.
 *
 * The function handles different types of periods:
 * - Single day periods (e.g., March 15, 2024 → March 14, 2024)
 * - Month periods (e.g., March 1-31, 2024 → February 1-29, 2024)
 * - Quarter periods (e.g., Q1 2024: Jan 1-Mar 31 → Q4 2023: Oct 1-Dec 31)
 * - Year periods (e.g., 2024: Jan 1-Dec 31 → 2023: Jan 1-Dec 31)
 * - Custom periods: Uses the exact number of days between start and end dates
 *   (e.g., March 1-15, 2024 → February 15-29, 2024)
 *
 * For custom periods, the function:
 * 1. Calculates the exact number of days between start and end dates
 * 2. Goes back that same number of days from the start date
 * 3. Returns a period of the same length
 *
 * For any period type (including custom periods), if the end date is in the future,
 * the previous period is calculated based on the current date. For example,
 * if today is March 15, 2024:
 *
 * - Input: March 1-31, 2024 → Output: February 1-15, 2024
 *
 * @param startDate - The start date of the current period
 * @param endDate - The end date of the current period
 * @returns An object containing the start and end dates of the previous period
 * in local time. This method returns the start of the day for the start date and
 * the end of the day for the end date.

 * @throws {Error} When end date is before start date
 *
 * @example
 * // Single day
 * calculatePreviousPeriod(new Date('2024-03-15'), new Date('2024-03-15'))
 * // Returns: { startDate: '2024-03-14', endDate: '2024-03-14' }

 * @example
 * // Month period
 * calculatePreviousPeriod(new Date('2024-03-01'), new Date('2024-03-31'))
 * // Returns: { startDate: '2024-02-01', endDate: '2024-02-29' }
 *
 * @example
 * // Year period (today is Feb 15 2024)
 * calculatePreviousPeriod(new Date('2024-01-01'), new Date('2024-12-31'))
 * // Returns: { startDate: '2023-01-01', endDate: '2023-02-15' }
 *
 * @example
 * // Custom period (15 days)
 * calculatePreviousPeriod(new Date('2024-03-01'), new Date('2024-03-15'))
 * // Returns: { startDate: '2024-02-15', endDate: '2024-02-29' }
 *
 */
import {
  addDays,
  differenceInDays,
  endOfDay,
  endOfMonth,
  endOfQuarter,
  endOfYear,
  isBefore,
  isFuture,
  isSameDay,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfYear,
  subDays,
  subMonths,
  subQuarters,
  subYears,
} from 'date-fns'

export const calculatePreviousPeriod = (startDate: Date, endDate: Date) => {
  const { startDate: _startDate, endDate: _endDate } = normalizeDates(startDate, endDate)

  if (isBefore(_endDate, _startDate)) {
    throw new Error('End date must be greater than start date')
  }

  if (isDayPeriod(_startDate, _endDate)) {
    return calculatePreviousDayPeriod(_startDate)
  }

  if (isMonthPeriod(_startDate, _endDate)) {
    return calculatePreviousMonthPeriod(_startDate, _endDate)
  }

  if (isQuarterPeriod(_startDate, _endDate)) {
    return calculatePreviousQuarterPeriod(_startDate, _endDate)
  }

  if (isYearPeriod(_startDate, _endDate)) {
    return calculatePreviousYearPeriod(_startDate, _endDate)
  }

  return calculatePreviousCustomPeriod(_startDate, _endDate)
}

const calculatePreviousDayPeriod = (startDate: Date) => {
  const previousDay = subDays(startDate, 1)
  return normalizeDates(previousDay, previousDay)
}

const calculatePreviousMonthPeriod = (startDate: Date, endDate: Date) => {
  const previousStartDate = subMonths(startDate, 1)
  const endOfPreviousMonth = endOfMonth(previousStartDate)
  const previousEndDate = isFuture(endDate)
    ? addDays(previousStartDate, calculateEffectivePeriodSize(startDate, endDate))
    : endOfPreviousMonth

  return normalizeDates(previousStartDate, previousEndDate)
}

const calculatePreviousQuarterPeriod = (startDate: Date, endDate: Date) => {
  const previousStartDate = subQuarters(startDate, 1)
  const endOfPreviousQuarter = endOfQuarter(previousStartDate)
  const previousEndDate = isFuture(endDate)
    ? addDays(previousStartDate, calculateEffectivePeriodSize(startDate, endDate))
    : endOfPreviousQuarter

  return normalizeDates(previousStartDate, previousEndDate)
}

const calculatePreviousYearPeriod = (startDate: Date, endDate: Date) => {
  const previousStartDate = subYears(startDate, 1)
  const endOfPreviousYear = endOfYear(previousStartDate)
  const previousEndDate = isFuture(endDate)
    ? addDays(previousStartDate, calculateEffectivePeriodSize(startDate, endDate))
    : endOfPreviousYear

  return normalizeDates(previousStartDate, previousEndDate)
}

const calculatePreviousCustomPeriod = (startDate: Date, endDate: Date) => {
  const periodSize = differenceInDays(endDate, startDate)
  const previousStartDate = subDays(startDate, periodSize + 1)
  const effectivePeriodSize = calculateEffectivePeriodSize(startDate, endDate)
  const previousEndDate = addDays(previousStartDate, effectivePeriodSize)

  return normalizeDates(previousStartDate, previousEndDate)
}

const calculateEffectivePeriodSize = (startDate: Date, endDate: Date) => {
  if (isFuture(endDate)) {
    return differenceInDays(new Date(), startDate)
  }
  return differenceInDays(endDate, startDate)
}

const isDayPeriod = (startDate: Date, endDate: Date): boolean => {
  return isSameDay(startDate, endDate)
}

const isMonthPeriod = (startDate: Date, endDate: Date): boolean => {
  const monthStart = startOfMonth(startDate)
  const monthEnd = endOfMonth(startDate)

  return isSameDay(startDate, monthStart) && isSameDay(endDate, monthEnd)
}

const isQuarterPeriod = (startDate: Date, endDate: Date): boolean => {
  const quarterStart = startOfQuarter(startDate)
  const quarterEnd = endOfQuarter(startDate)

  return isSameDay(startDate, quarterStart) && isSameDay(endDate, quarterEnd)
}

const isYearPeriod = (startDate: Date, endDate: Date): boolean => {
  const yearStart = startOfYear(startDate)
  const yearEnd = endOfYear(startDate)

  return isSameDay(startDate, yearStart) && isSameDay(endDate, yearEnd)
}

const normalizeDates = (startDate: Date, endDate: Date) => {
  return {
    startDate: startOfDay(startDate),
    endDate: endOfDay(endDate),
  }
}
